import { useEffect, useState } from 'react';
import { Config } from '../config';
import { useCountries } from './useCountries';

export interface EntranceRevisitPromotionStatus {
    activated: boolean;
    discountPercentage?: number;
    maxDiscountedChildren?: number;
    validityDurationInDays?: number;
}

/**
 * Hook to fetch the entrance revisit promotion status for the current country.
 */
export function useEntranceRevisitPromotionStatus() {
    const { currentCountry } = useCountries();
    const [status, setStatus] = useState<EntranceRevisitPromotionStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        const abortController = new AbortController();

        const fetchStatus = async () => {
            try {
                setLoading(true);
                const response = await fetch(`${Config.baseUrlApi}/api/v2/entrance-revisit-promotions/status`, {
                    headers: {
                        'Accept-Language': `${currentCountry.language}-${currentCountry.code}`,
                    },
                    signal: abortController.signal,
                });

                if (!response.ok) {
                    throw new Error(`Failed to fetch entrance revisit promotion status: ${response.statusText}`);
                }

                const data: EntranceRevisitPromotionStatus = await response.json();
                setStatus(data);
                setError(null);
            } catch (err) {
                if (!abortController.signal.aborted) {
                    setError(err instanceof Error ? err : new Error('Unknown error'));
                }
                setStatus(null);
            } finally {
                setLoading(false);
            }
        };

        fetchStatus();

        return () => abortController.abort();
    }, [currentCountry.language, currentCountry.code]);

    return { status, loading, error };
}
